'use client'

import { useCallback, useState } from 'react'
import Button from '@components/Furry/Button'
import { useTranslation } from 'react-i18next'
import { bulkSendRawTransactions } from '@helium/spl-utils'
import { IdlAccounts, Provider } from '@coral-xyz/anchor'
import { PublicKey, Transaction } from '@solana/web3.js'
import { AuctionManager } from '@dewicats/idls/lib/types/auction_manager'
import { useAsync } from 'react-async-hook'
import {
  init as initAuctionManager,
  referralRecipientKey,
} from '@dewicats/auction-manager-sdk'
import { useSolana } from '@dewicats/connect-button'
import SimpleSpinner from '@components/SimpleSpinner'
import { isPast } from 'date-fns'
import {
  getMint,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token'

const ClaimButton = ({
  recipient,
  holding,
}: {
  recipient: IdlAccounts<AuctionManager>['referralRecipientV0'] & {
    publicKey: PublicKey
    listingAcc: IdlAccounts<AuctionManager>['listingV0']
  }
  holding: { name: string }
}) => {
  const [claiming, setClaiming] = useState(false)
  const [claimed, setClaimed] = useState(false)
  const { t } = useTranslation()

  const { anchorProvider } = useSolana()

  const { result: auctionProgram } = useAsync(() => {
    return initAuctionManager(anchorProvider)
  }, [anchorProvider])

  const is2022 = async (provider: Provider, mint: PublicKey) => {
    try {
      const mintInfo = await getMint(
        provider.connection,
        mint,
        'confirmed',
        TOKEN_2022_PROGRAM_ID,
      )

      return !!mintInfo
    } catch (e) {
      return false
    }
  }

  const claimRewards = useCallback(
    async (
      auctionManager: PublicKey,
      listing: PublicKey,
      tokenMint: PublicKey,
      nft: PublicKey,
      nftName: string,
    ) => {
      if (auctionProgram) {
        const { instruction } = await auctionProgram.methods
          .claimReferralRewardsV0({
            recipientWallet: anchorProvider.publicKey,
          })
          .accountsPartial({
            auctionManager,
            referralRecipient: referralRecipientKey(listing, nftName)[0],
            listing,
            tokenMint,
            nft,
          })
          .prepare()

        const txn = new Transaction()

        txn.add(instruction)

        txn.feePayer = anchorProvider.publicKey
        txn.recentBlockhash = (
          await anchorProvider.connection.getLatestBlockhash()
        ).blockhash

        const signedTx = await anchorProvider.wallet.signTransaction(txn)

        const sigs = await bulkSendRawTransactions(anchorProvider?.connection, [
          signedTx.serialize(),
        ])

        if (!sigs || sigs.length === 0) {
          throw new Error('Transaction Error')
        }

        return sigs
      }
    },
    [anchorProvider, auctionProgram],
  )

  if (
    recipient.claimed ||
    claimed ||
    !isPast(new Date(recipient.listingAcc.endAt.toNumber() * 1000))
  ) {
    return null
  }

  return (
    <Button
      ripple
      className="text-xs px-8"
      disabled={!isPast(recipient.listingAcc.endAt.toNumber() * 1000)}
      onClick={async () => {
        try {
          setClaiming(true)
          await claimRewards(
            recipient.listingAcc.auctionManager,
            recipient.listing,
            recipient.listingAcc.tokenMint,
            recipient.nft,
            holding.name,
          )
          setClaimed(true)
        } catch {}
        setClaiming(false)
      }}
    >
      {claiming ? (
        <SimpleSpinner size={'small'} />
      ) : (
        t('holdings.details.claim')
      )}
    </Button>
  )
}

export default ClaimButton
